// Membership and VIP system

export interface Membership {
  id: string
  name: string
  emoji: string
  monthlyFee: number
  benefits: MembershipBenefit[]
  maxMembers: number
  requirements: MembershipRequirement[]
}

export interface MembershipBenefit {
  type: 'discount' | 'priority_booking' | 'free_hours' | 'guest_passes' | 'shop_discount'
  value: number
  description: string
}

export interface MembershipRequirement {
  type: 'reputation' | 'building' | 'courts'
  value: number | string
}

export const MEMBERSHIP_TIERS: Membership[] = [
  {
    id: 'basic',
    name: 'Socio Básico',
    emoji: '🎾',
    monthlyFee: 30,
    benefits: [
      { type: 'discount', value: 10, description: '10% descuento en reservas' },
    ],
    maxMembers: 50,
    requirements: [
      { type: 'reputation', value: 2 },
    ],
  },
  {
    id: 'standard',
    name: 'Socio Estándar',
    emoji: '⭐',
    monthlyFee: 60,
    benefits: [
      { type: 'discount', value: 15, description: '15% descuento en reservas' },
      { type: 'free_hours', value: 2, description: '2 horas gratis al mes' },
      { type: 'shop_discount', value: 10, description: '10% en Pro Shop' },
    ],
    maxMembers: 100,
    requirements: [
      { type: 'reputation', value: 3 },
      { type: 'building', value: 'reception' },
    ],
  },
  {
    id: 'premium',
    name: 'Socio Premium',
    emoji: '💎',
    monthlyFee: 120,
    benefits: [
      { type: 'discount', value: 25, description: '25% descuento en reservas' },
      { type: 'priority_booking', value: 1, description: 'Reserva prioritaria' },
      { type: 'free_hours', value: 5, description: '5 horas gratis al mes' },
      { type: 'guest_passes', value: 2, description: '2 pases invitado/mes' },
      { type: 'shop_discount', value: 20, description: '20% en Pro Shop' },
    ],
    maxMembers: 50,
    requirements: [
      { type: 'reputation', value: 4 },
      { type: 'building', value: 'locker' },
      { type: 'courts', value: 3 },
    ],
  },
  {
    id: 'vip',
    name: 'Socio VIP',
    emoji: '👑',
    monthlyFee: 250,
    benefits: [
      { type: 'discount', value: 40, description: '40% descuento en reservas' },
      { type: 'priority_booking', value: 1, description: 'Reserva prioritaria' },
      { type: 'free_hours', value: 10, description: '10 horas gratis al mes' },
      { type: 'guest_passes', value: 5, description: '5 pases invitado/mes' },
      { type: 'shop_discount', value: 30, description: '30% en Pro Shop' },
    ],
    maxMembers: 20,
    requirements: [
      { type: 'reputation', value: 4.5 },
      { type: 'building', value: 'cafe' },
      { type: 'courts', value: 5 },
    ],
  },
]

// Training academy courses
export interface Course {
  id: string
  name: string
  emoji: string
  description: string
  pricePerSession: number
  duration: number // hours
  groupSize: number
  level: 'beginner' | 'intermediate' | 'advanced' | 'elite'
  requirements: CourseRequirement[]
}

export interface CourseRequirement {
  type: 'staff' | 'courts'
  value: string | number
}

export const ACADEMY_COURSES: Course[] = [
  {
    id: 'initiation',
    name: 'Iniciación al Pádel',
    emoji: '🎓',
    description: 'Curso para principiantes absolutos',
    pricePerSession: 15,
    duration: 1,
    groupSize: 4,
    level: 'beginner',
    requirements: [
      { type: 'staff', value: 'coach' },
    ],
  },
  {
    id: 'technique',
    name: 'Técnica Mejorada',
    emoji: '📚',
    description: 'Mejora tu técnica y golpes',
    pricePerSession: 25,
    duration: 1.5,
    groupSize: 4,
    level: 'intermediate',
    requirements: [
      { type: 'staff', value: 'coach' },
      { type: 'courts', value: 2 },
    ],
  },
  {
    id: 'tactics',
    name: 'Táctica Avanzada',
    emoji: '🧠',
    description: 'Estrategia y posicionamiento en pista',
    pricePerSession: 40,
    duration: 2,
    groupSize: 4,
    level: 'advanced',
    requirements: [
      { type: 'staff', value: 'coach' },
      { type: 'courts', value: 3 },
    ],
  },
  {
    id: 'elite_training',
    name: 'Entrenamiento Élite',
    emoji: '🏆',
    description: 'Preparación para competiciones',
    pricePerSession: 80,
    duration: 2,
    groupSize: 2,
    level: 'elite',
    requirements: [
      { type: 'staff', value: 'coach' },
      { type: 'courts', value: 4 },
    ],
  },
  {
    id: 'kids_camp',
    name: 'Campus Infantil',
    emoji: '👶',
    description: 'Clases para niños de 6-12 años',
    pricePerSession: 20,
    duration: 2,
    groupSize: 8,
    level: 'beginner',
    requirements: [
      { type: 'staff', value: 'coach' },
    ],
  },
]

// Maintenance and repair system
export interface MaintenanceTask {
  id: string
  name: string
  emoji: string
  description: string
  cost: number
  duration: number // days
  conditionRestore: number // percentage points restored
  applicableTo: 'court' | 'building' | 'all'
}

export const MAINTENANCE_TASKS: MaintenanceTask[] = [
  {
    id: 'quick_clean',
    name: 'Limpieza Rápida',
    emoji: '🧹',
    description: 'Limpieza básica de la pista',
    cost: 50,
    duration: 0,
    conditionRestore: 5,
    applicableTo: 'court',
  },
  {
    id: 'deep_clean',
    name: 'Limpieza Profunda',
    emoji: '🧼',
    description: 'Limpieza completa y desinfección',
    cost: 150,
    duration: 1,
    conditionRestore: 15,
    applicableTo: 'all',
  },
  {
    id: 'surface_repair',
    name: 'Reparación Superficie',
    emoji: '🔧',
    description: 'Arreglar césped dañado y parches',
    cost: 400,
    duration: 2,
    conditionRestore: 30,
    applicableTo: 'court',
  },
  {
    id: 'glass_replacement',
    name: 'Cambio de Cristales',
    emoji: '🪟',
    description: 'Reemplazar cristales dañados',
    cost: 800,
    duration: 3,
    conditionRestore: 40,
    applicableTo: 'court',
  },
  {
    id: 'full_renovation',
    name: 'Renovación Completa',
    emoji: '🏗️',
    description: 'Renovación total de la pista',
    cost: 2000,
    duration: 5,
    conditionRestore: 100,
    applicableTo: 'court',
  },
  {
    id: 'hvac_service',
    name: 'Mantenimiento HVAC',
    emoji: '❄️',
    description: 'Revisión aire acondicionado',
    cost: 300,
    duration: 1,
    conditionRestore: 20,
    applicableTo: 'building',
  },
]

// Calculate monthly membership income
export function calculateMembershipIncome(
  memberCounts: Record<string, number>
): number {
  let total = 0
  for (const tier of MEMBERSHIP_TIERS) {
    const count = memberCounts[tier.id] || 0
    total += count * tier.monthlyFee
  }
  return total
}

// Calculate course income per week
export function calculateCourseIncome(
  activeCourses: string[],
  sessionsPerWeek: number = 5
): number {
  let total = 0
  for (const courseId of activeCourses) {
    const course = ACADEMY_COURSES.find(c => c.id === courseId)
    if (course) {
      total += course.pricePerSession * course.groupSize * sessionsPerWeek
    }
  }
  return total
}
